import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Text from "../../atoms/Text";
import DrugSearchConditionForm, {
  DrugSearchConditionState,
  toInputValue,
  parseIdentifyTextInput
} from "../DrugSearchConditionForm";
import DrugSearchResultList from "../DrugSearchResultList";
import { searchDrugs, DrugSearchResultItem, DrugSearchParams } from "../../../api/drugIdentificationService";

// 초기 조건 타입 (테이블에서 전달받는 조건)
export interface InitialSearchCondition {
  identifyText1?: string | string[];
  identifyText2?: string | string[];
  formulation?: string[];
  shape?: string[];
  color?: string[];
  line?: string[];
}

interface DrugSearchPanelProps {
  initialCondition?: InitialSearchCondition;
}

// 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`;

const Header = styled.div`
  margin-bottom: 8px;
`;

const Title = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
`;

const DrugSearchPanel: React.FC<DrugSearchPanelProps> = ({
  initialCondition
}) => {
  const [condition, setCondition] = useState<DrugSearchConditionState>({
    identifyText1: '',
    identifyText2: '',
    ignoreText1: false,
    ignoreText2: false,
    formulation: [],
    shape: [],
    color: [],
    line: [],
    limit: 10
  });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<DrugSearchResultItem[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // initialCondition이 변경되면 조건 업데이트
  useEffect(() => {
    if (initialCondition) {
      setCondition({
        identifyText1: toInputValue(initialCondition.identifyText1),
        identifyText2: toInputValue(initialCondition.identifyText2),
        ignoreText1: false,
        ignoreText2: false,
        formulation: initialCondition.formulation || [],
        shape: initialCondition.shape || [],
        color: initialCondition.color || [],
        line: initialCondition.line || [],
        limit: 10
      });
      // 초기 조건이 있으면 결과 초기화
      setResults([]);
      setHasSearched(false);
    }
  }, [initialCondition]);

  const handleSearch = async () => {
    try {
      setSearching(true);

      const params: DrugSearchParams = {
        identifyText1: condition.ignoreText1 ? null : parseIdentifyTextInput(condition.identifyText1),
        identifyText2: condition.ignoreText2 ? null : parseIdentifyTextInput(condition.identifyText2),
        formulation: condition.formulation,
        shape: condition.shape,
        color: condition.color,
        line: condition.line,
        limit: condition.limit
      };

      const response = await searchDrugs(params);
      setResults(response.data.results || []);
      setHasSearched(true);
    } catch (error) {
      console.error("Failed to search drugs:", error);
      alert("의약품 검색에 실패했습니다.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>의약품 검색</Title>
      </Header>

      <ContentWrapper>
        <DrugSearchConditionForm
          condition={condition}
          onChange={setCondition}
          onSearch={handleSearch}
          searching={searching}
          alwaysEditMode={true}
          showCancelButton={false}
        />

        {hasSearched && (
          <DrugSearchResultList
            results={results}
            disableScroll={true}
          />
        )}
      </ContentWrapper>
    </Container>
  );
};

export default DrugSearchPanel;
